import { Row } from 'app/design/primitives/box';
import { selectedCategoryAtom, selectedServiceCategoryAtom } from 'app/atoms';
import { CategoryTab } from 'app/design/categories/tab';
import { SelectPickerHtml } from 'app/design/primitives/select/select-html';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { getI18nLanguage } from 'app/lib/language';
import { useAllCategories, useAllServiceCategories, type Category } from 'app/network/category';
import { useSetAtom } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { navigateToListingWithParams } from 'app/screens/billboard/navigator-helper';

export function CategoryPicker({
  isService,
  selectedCategoryId,
  goBackTo,
  listingId,
  onPress,
}: {
  isService: boolean;
  selectedCategoryId?: string | null;
  goBackTo: 'new' | 'one-listing' | 'edit-listing' | 'set-atom-then-close';
  listingId?: string;
  onPress?: () => void;
}) {
  const { t } = useTranslation('new');
  const i18nLanguage = getI18nLanguage();
  const openNewWithGuard = useOpenNewWithGuard();
  const navigation = useNavigation();
  const { data: serviceCategories } = useAllServiceCategories();
  const { data: categories } = useAllCategories();

  const setSelectedCategory = useSetAtom(isService ? selectedServiceCategoryAtom : selectedCategoryAtom);

  const onCategorySelected = (categoryId: Category['id']) => {
    // navigation.popTo()
    if (goBackTo === 'new') {
      openNewWithGuard(navigation, {
        newCategoryId: categoryId,
      });
    }
    if (goBackTo === 'one-listing') {
      navigateToListingWithParams(navigation, {
        id: listingId!,
        newCategoryId: categoryId,
      });
    }
    if (goBackTo === 'edit-listing') {
      navigation.navigate(
        'edit-listing',
        {
          id: listingId!,
          newCategoryId: categoryId,
        },
        { pop: true },
      );
    }
    if (goBackTo === 'set-atom-then-close') {
      setSelectedCategory(categoryId === selectedCategoryId ? [] : [categoryId]);
      navigation.goBack();
    }
  };

  const categoryOptions = (() => {
    const categoriesData = isService ? serviceCategories : categories;

    if (!categoriesData) return [];

    return categoriesData.map((category) => {
      // const categoryName = isService
      //   ? t(`serviceCategories.${typeof category.name === 'object' ? category.name[i18nLanguage] || category.name.en : category.name}.name`, { ns: 'common' })
      //   : (typeof category.name === 'object' ? category.name[i18nLanguage] : category.name) || category.name;

      const categoryIcon = 'icon' in category ? category.icon : 'other';
      const categoryName = typeof category.name === 'object' ? category.name[i18nLanguage] : category.name;

      return {
        key: category.id,
        value: (
          <Row alignItems="center" gap="spacing.general.x2">
            <CategoryTab category={categoryIcon || 'other'} size={20} />
            <Text fontSize={14}>{categoryName}</Text>
          </Row>
        ),
      };
    });
  })();

  const selectedCategory = isService
    ? serviceCategories?.map((c) => ({ ...c, icon: undefined })).find((c) => c.id === selectedCategoryId)
    : categories
        ?.map((c) => ({ id: c.id, name: c.name[i18nLanguage], icon: c.icon }))
        .find((c) => c.id === selectedCategoryId);

  const defaultOption = (() => {
    if (!selectedCategory) return undefined;
    const categoryName = isService
      ? t(
          `serviceCategories.${typeof selectedCategory.name === 'object' ? selectedCategory.name : selectedCategory.name}.name`,
          { ns: 'common' },
        )
      : (typeof selectedCategory.name === 'object' ? selectedCategory.name[i18nLanguage] : selectedCategory.name) ||
        selectedCategory.name;

    const categoryIcon = 'icon' in selectedCategory ? selectedCategory.icon : 'other';

    return {
      key: selectedCategory.id,
      value: (
        <Row alignItems="center" gap="spacing.general.x2">
          <CategoryTab category={categoryIcon || 'other'} size={20} />
          <Text fontSize={14}>{categoryName}</Text>
        </Row>
      ),
    };
  })();

  return (
    <SelectPickerHtml
      data={categoryOptions}
      defaultOption={defaultOption}
      setSelected={(categoryId) => {
        onCategorySelected(categoryId);
        onPress?.();
      }}
      placeholder={t('categorySection.primary.placeholder')}
      maxHeight={300}
    />
  );
}
